// src/pages/inventory/settings/ShopifySettings.tsx
import React, { useState, useEffect } from "react";
import { Button, Typography, Space, Descriptions, Statistic, Row, Col, Tabs, Modal, Input, Form } from "antd";
import { ShoppingOutlined, CheckCircleOutlined, CloseCircleOutlined, ImportOutlined, MergeCellsOutlined, UserOutlined, PlusOutlined } from "@ant-design/icons";
import { getShopifyConfig, disconnectShopify, initiateShopifyInstall } from "../../../api/inventory/shopifyApi";
import { useNotifications } from "../../../context/NotificationContext";
import ProductMergeTab from "./shopify/ProductMergeTab";
import OrderSyncTab from "./shopify/OrderSyncTab";
import CustomerSyncTab from "./shopify/CustomerSyncTab";
import ProductSyncTab from "./shopify/ProductSyncTab";
import ShopifyCredentialsSection from "./shopify/ShopifyCredentialsSection";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { SectionCard, StatusTag, InlineLoading, EmptyState } from "../../../components/shared";

const { Text, Paragraph } = Typography;

const ShopifySettings: React.FC = () => {
	const [config, setConfig] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [disconnecting, setDisconnecting] = useState(false);
	const [activeTab, setActiveTab] = useState("overview");
	const [installModalVisible, setInstallModalVisible] = useState(false);
	const [connecting, setConnecting] = useState(false);
	const [form] = Form.useForm();
	const { showError, showSuccess, showInfo } = useNotifications();

	useEffect(() => {
		loadShopifyConfig();
	}, []);

	const loadShopifyConfig = async () => {
		setLoading(true);
		try {
			const shopifyConfig = await getShopifyConfig();
			setConfig(shopifyConfig);
		} catch (error) {
			showInfo("Your Shopify integration is currently inactive. Please check your settings.");
		} finally {
			setLoading(false);
		}
	};

	const handleDisconnect = () => {
		Modal.confirm({
			title: "Disconnect Shopify Store",
			content: (
				<div>
					<Paragraph>Are you sure you want to disconnect your Shopify store? This will:</Paragraph>
					<ul>
						<li>Deactivate the integration</li>
						<li>Stop automatic syncing</li>
						<li>Preserve all existing product and order data</li>
					</ul>
					<Paragraph type="warning">You can reconnect at any time to re-enable syncing and register webhooks.</Paragraph>
				</div>
			),
			okText: "Disconnect",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				setDisconnecting(true);
				try {
					await disconnectShopify();
					showSuccess("Shopify store disconnected successfully");
					// Re-fetch rather than locally nulling `config`: disconnect only
					// deactivates the backend row (is_active: false) and preserves it
					// (shop_domain/credentials intact) so it can be reconnected. Nulling
					// here would make the credentials section think there's no row at
					// all — POSTing a duplicate on save instead of PUTing the existing
					// one, and losing the shop_domain-reuse guard until a page reload.
					await loadShopifyConfig();
				} catch (error) {
					showError("Failed to disconnect Shopify store");
				} finally {
					setDisconnecting(false);
				}
			},
		});
	};

	const handleConnectShopify = () => {
		setInstallModalVisible(true);
	};

	const handleInstallSubmit = async (values: { storeName: string }) => {
		try {
			setConnecting(true);
			const user = getSessionUser();
			const cleanStoreName = values.storeName.replace(/\.myshopify\.com$/, "").replace(/^https?:\/\/(www\.)?/, "");
			const response = await initiateShopifyInstall({ shop: cleanStoreName });
			if (response.install_url) {
				window.open(response.install_url, "_blank");
				showSuccess("Redirecting to Shopify for authorization. Complete the installation in the new tab.");
				setInstallModalVisible(false);
				form.resetFields();
				setTimeout(() => loadShopifyConfig(), 2000);
			} else {
				showError("Failed to get installation URL from server");
			}
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to connect to Shopify. Please check your store name and try again.");
		} finally {
			setConnecting(false);
		}
	};

	const handleInstallCancel = () => {
		setInstallModalVisible(false);
		form.resetFields();
	};

	// Once a ShopifyConfig row already exists (created via the Shopify App
	// Credentials form below), "Connect Store" reuses its shop_domain instead
	// of re-prompting for a free-typed one — a typo/case mismatch between the
	// two entry points would otherwise silently break shop_domain lookups.
	const handleConnectExisting = async (shopDomain: string) => {
		try {
			setConnecting(true);
			const response = await initiateShopifyInstall({ shop: shopDomain });
			if (response.install_url) {
				window.open(response.install_url, "_blank");
				showSuccess("Redirecting to Shopify for authorization. Complete the installation in the new tab.");
				setTimeout(() => loadShopifyConfig(), 2000);
			} else {
				showError("Failed to get installation URL from server");
			}
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to connect to Shopify. Please try again.");
		} finally {
			setConnecting(false);
		}
	};

	if (loading) {
		return <InlineLoading message="Loading Shopify configuration…" minHeight={300} />;
	}

	if (!config || !config.is_active) {
		const hasPreCreatedDomain = !!config?.shop_domain;

		return (
			<Space direction="vertical" style={{ width: "100%" }} size={16}>
				<ShopifyCredentialsSection config={config} onSaved={setConfig} />

				{hasPreCreatedDomain ? (
					<SectionCard title="Connect Store">
						<Space direction="vertical" style={{ width: "100%" }} size={12}>
							<Text>
								Store: <Text strong>{config!.shop_domain}</Text>
							</Text>
							<Text type="secondary" style={{ fontSize: 12 }}>
								This uses the shop domain saved in your Shopify App Credentials above. Update it there if it needs to change.
							</Text>
							<Button type="primary" icon={<PlusOutlined />} loading={connecting} onClick={() => handleConnectExisting(config!.shop_domain)}>
								Connect to Shopify
							</Button>
						</Space>
					</SectionCard>
				) : (
					<>
						<EmptyState
							title="Shopify Not Connected"
							description="Connect your Shopify store to sync products, orders, and customers."
							action={{ label: "Connect Shopify Store", onClick: handleConnectShopify }}
						/>

						<Modal title="Connect Shopify Store" open={installModalVisible} onCancel={handleInstallCancel} footer={null} width={500}>
							<Form form={form} layout="vertical" onFinish={handleInstallSubmit}>
								<div style={{ marginBottom: 16 }}>
									<Text type="secondary">Enter your Shopify store name to connect your store with our system. You'll be redirected to Shopify to authorize the connection.</Text>
								</div>
								<Form.Item
									name="storeName"
									label="Store Name"
									rules={[
										{ required: true, message: "Please enter your store name" },
										{ pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*$/, message: "Store name should only contain letters, numbers, and hyphens" },
									]}
									extra="Enter just your store name (e.g., 'my-store' not 'my-store.myshopify.com')"
								>
									<Input placeholder="my-store-name" suffix=".myshopify.com" autoComplete="off" />
								</Form.Item>
								<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
									<Button onClick={handleInstallCancel} disabled={connecting}>Cancel</Button>
									<Button type="primary" htmlType="submit" loading={connecting} icon={<PlusOutlined />}>Connect Store</Button>
								</div>
							</Form>
						</Modal>
					</>
				)}
			</Space>
		);
	}

	const tabItems = [
		{
			key: "overview",
			label: <Space><ShoppingOutlined />Overview</Space>,
			children: (
				<Space direction="vertical" style={{ width: "100%" }} size={16}>
					<Row gutter={[16, 16]}>
						<Col xs={24} lg={12}>
							<SectionCard>
								<Statistic
									title="Connection Status"
									value={config.is_active ? "Connected" : "Disconnected"}
									valueStyle={{ color: config.is_active ? "var(--mg-success)" : "var(--mg-danger)" }}
									prefix={config.is_active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
								/>
							</SectionCard>
						</Col>
						<Col xs={24} lg={12}>
							<SectionCard>
								<Statistic title="Shop Domain" value={config.shop_domain} prefix={<ShoppingOutlined />} />
							</SectionCard>
						</Col>
					</Row>

					<SectionCard title="Store Information">
						<Descriptions column={2} bordered>
							<Descriptions.Item label="Shop Domain">{config.shop_domain}</Descriptions.Item>
							<Descriptions.Item label="API Version">{config.api_version}</Descriptions.Item>
							<Descriptions.Item label="Location ID">{config.shopify_location_id || "Not set"}</Descriptions.Item>
							<Descriptions.Item label="Status">
								<StatusTag status={config.is_active ? "active" : "inactive"} />
							</Descriptions.Item>
						</Descriptions>
					</SectionCard>

					<SectionCard title="Integration Guide">
						<ul style={{ margin: 0, paddingLeft: 20 }}>
							<li><Text><strong>Product Sync:</strong> Import products from Shopify and keep inventory synchronized</Text></li>
							<li><Text><strong>Order Import:</strong> Import orders with customer and line item details</Text></li>
							<li><Text><strong>Customer Sync:</strong> Import and update customer information</Text></li>
							<li><Text><strong>Product Merging:</strong> Resolve duplicate products when importing</Text></li>
							<li><Text><strong>Automatic Webhooks:</strong> Real-time updates when connected</Text></li>
						</ul>
					</SectionCard>

					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<Button danger icon={<CloseCircleOutlined />} onClick={handleDisconnect} loading={disconnecting}>
							Disconnect Store
						</Button>
					</div>
				</Space>
			),
		},
		{
			key: "products",
			label: <Space><ImportOutlined />Product Sync</Space>,
			children: <ProductSyncTab />,
		},
		{
			key: "customers",
			label: <Space><UserOutlined />Customer Sync</Space>,
			children: <CustomerSyncTab />,
		},
		{
			key: "orders",
			label: <Space><ImportOutlined />Order Sync</Space>,
			children: <OrderSyncTab />,
		},
		{
			key: "merge-requests",
			label: <Space><MergeCellsOutlined />Product Merges</Space>,
			children: <ProductMergeTab />,
		},
	];

	return <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />;
};

export default ShopifySettings;
